import React, { useState, useEffect } from 'react';
import { Shield, Heart, Ear, Scale, Sword, Star, Lock, Check, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const BRANCH_ICONS = {
  ear: Ear,
  shield: Shield,
  heart: Heart,
  scale: Scale,
  sword: Sword,
};

const SkillTree = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/skill-tree')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading skill tree...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load skill tree</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="skill-tree-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <TrendingUp size={28} className="text-indigo-600" />
            Conversation Skill Tree
          </h1>
          <p className="text-sm text-gray-500 mt-1">Level up your social superpowers</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <span className="font-bold text-indigo-600">{data.total_xp} Total XP</span>
            <span className="text-gray-300">|</span>
            <span className="font-bold text-amber-600">{data.skills_mastered}/5 Mastered</span>
          </div>
        </div>

        {/* Branches */}
        <div className="space-y-4">
          {data.branches.map(branch => {
            const Icon = BRANCH_ICONS[branch.icon] || Star;
            const isExpanded = expanded === branch.branch_id;

            return (
              <div
                key={branch.branch_id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all"
                data-testid={`skill-branch-${branch.branch_id}`}
              >
                {/* Branch Header */}
                <button
                  onClick={() => setExpanded(isExpanded ? null : branch.branch_id)}
                  className="w-full p-4 sm:p-5 flex items-center gap-4 hover:bg-gray-50 transition-all"
                  data-testid={`expand-${branch.branch_id}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${branch.color}15` }}
                  >
                    <Icon size={24} style={{ color: branch.color }} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900">{branch.name}</h3>
                      {branch.max_level && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full">MASTERED</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Lv.{branch.current_level} — {branch.level_name}</p>
                    {/* Progress Bar */}
                    <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${branch.progress_to_next}%`, backgroundColor: branch.color }}
                      />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-gray-400">{branch.xp} XP</span>
                      <span className="text-[10px] text-gray-400">Next: {branch.next_level_name}</span>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                </button>

                {/* Expanded Levels */}
                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-500 mb-3">{branch.description}</p>
                    <div className="space-y-2">
                      {branch.levels.map(lvl => {
                        const isUnlocked = lvl.level <= branch.current_level;
                        const isCurrent = lvl.level === branch.current_level;
                        return (
                          <div
                            key={lvl.level}
                            className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                              isCurrent ? 'border-2 bg-opacity-5' : isUnlocked ? 'bg-gray-50' : 'bg-gray-50 opacity-50'
                            }`}
                            style={isCurrent ? { borderColor: branch.color, backgroundColor: `${branch.color}08` } : {}}
                            data-testid={`level-${branch.branch_id}-${lvl.level}`}
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isUnlocked ? 'text-white' : 'bg-gray-200 text-gray-400'
                              }`}
                              style={isUnlocked ? { backgroundColor: branch.color } : {}}
                            >
                              {isUnlocked ? <Check size={14} /> : <Lock size={12} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold ${isUnlocked ? 'text-gray-900' : 'text-gray-400'}`}>
                                Lv.{lvl.level} — {lvl.name}
                              </p>
                              <p className="text-[10px] text-gray-400 truncate">{lvl.description}</p>
                              {!isUnlocked && (
                                <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                                  <Lock size={8} /> {lvl.unlock}
                                </p>
                              )}
                            </div>
                            <span className="text-[10px] font-bold text-gray-400">{lvl.xp_required} XP</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SkillTree;
